import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { RfcType } from "../../types/rfc-type";
import { BaseService } from "../../services/base-service";
import { URLS } from "../../services/app-urls";
import { Customer } from "../../models/general/customer";
import { Component } from "../../models/general/component";
import { translate } from "../../translate/translate";
import { useImportRfcSap } from "../../page/general/import/sap-rfc/import-rfc-sap/useImportRfcSap";
import AutocompletePaginated from "../autocomplete/autocompletePaginated";

interface DialogRfcRunProps {
  open: boolean;
  onClose: () => void;
  rfcType?: RfcType;
  onRefresh?: () => void;
  onTaskCreated?: (taskId?: string) => void;
}

export function DialogRfcRun({
  open,
  onClose,
  rfcType,
  onRefresh,
  onTaskCreated,
}: DialogRfcRunProps) {
  const DEFAULT_CUSTOMER_NAME = "DELL";
  const [plant] = useState("451N");
  const [client, setClient] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [selectedMaterials, setSelectedMaterials] = useState<Component[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const customerService = new BaseService<Customer>(URLS.CUSTOMER);
  const materialService = new BaseService<Component>(URLS.COMPONENT);

  const handleSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
    onClose();
  };

  const { isLoading, handleExecuteImportForecast, handleExecuteImportBom } = useImportRfcSap(
    rfcType,
    handleSuccess,
    onTaskCreated,
  );

  const isForecast = rfcType === RfcType.FORECAST;
  const isStockPo = rfcType === RfcType.STOCK_AND_PO;
  const isBom = rfcType === RfcType.BOM;

  const handleExecute = async () => {
    if (isForecast && client) {
      const customerId = parseInt(client, 10);
      await handleExecuteImportForecast(plant, customerId.toString());
    } else if (isBom && selectedMaterials.length > 0) {
      const partNumbers = selectedMaterials
        .map((material) => material.part_number)
        .filter((partNumber): partNumber is string => !!partNumber);
      await handleExecuteImportBom(plant, partNumbers);
    }
  };

  useEffect(() => {
    if (open && isForecast) {
      customerService.clearParameters();
      customerService.getAll().then((response) => {
        const loadedCustomers = response.data;
        setCustomers(loadedCustomers);

        const defaultCustomer = loadedCustomers.find(
          (customer) => customer.name?.trim().toUpperCase() === DEFAULT_CUSTOMER_NAME
        );

        setClient(defaultCustomer?.id?.toString() ?? "");
      }).catch((error) => {
        console.error("Erro ao carregar clientes:", error);
      });
    }
  }, [open, isForecast]);

  useEffect(() => {
    if (open && isBom) {
      materialService.clearParameters();
    }
  }, [open, isBom]);

  const dialogTitle = isForecast
    ? "RFC_FORECAST_DATA"
    : isStockPo
    ? "RFC_PO_DATA"
    : isBom
    ? "RFC_BOM_DATA"
    : "RFC";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#313ab9ff",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5, //
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {dialogTitle}

        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={translate.t("plant")}
            value={plant}
            size="small"
            fullWidth
            disabled
          />

          {isForecast && (
            <>
              <TextField
                select
                label={translate.t("customer")}
                value={client}
                onChange={(e) => setClient(e.target.value)}
                size="small"
                fullWidth
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                {translate.t("rfc_system")}
              </Typography>
            </>
          )}

          {isStockPo && (
            <DatePicker
              label={translate.t("date")}
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                },
              }}
            />
          )}

          {isBom && (
            <AutocompletePaginated
              service={materialService}
              label={translate.t("materials")}
              searchField="part_number"
              display="part_number"
              onSelectElement={(materials: Component[]) =>
                setSelectedMaterials(materials)
              }
              element={selectedMaterials}
              isMultiSelect={true}
              required={false}
              fullWidth
              filters={{
                is_final_product: true,
                is_in_test: false,
              }}
            />
          )}
        </Box>
      </DialogContent>


      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          gap: 1.5,
          justifyContent: "flex-end",
        }}
      >
        <Button variant="outlined" size="small" onClick={onClose}>
          {translate.t("close")}
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={handleExecute}
          disabled={isLoading || (isForecast && !client) || (isBom && selectedMaterials.length === 0)}
        >
          {translate.t("execute")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
